"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import type { DefaultValues, UseFormReturn } from "react-hook-form";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItemWithoutDiv,
  UnstyledFormLabel,
} from "@/components/ui/form";

import {
  CurrencyInput,
  floorToTwoDecimals,
  LabelCell,
  NumberDisplayCell,
  Table,
  TableHeader,
  Tr,
} from "./shared";

const formSchema = z.object({
  netSellingPrice: z.number().positive(),
  grossProfitPerUnit: z.number().positive(),
  campaignSpend: z.number().positive(),
  campaignRoas: z.number().positive(),
});

const defaultValues = {
  netSellingPrice: 100,
  grossProfitPerUnit: 81,
  campaignSpend: 50_000,
  campaignRoas: 2,
} satisfies DefaultValues<z.infer<typeof formSchema>>;

interface ContentProps {
  form: UseFormReturn<z.infer<typeof formSchema>>;
  breakevenRoas: number;
  unitsSold: number;
  adSpendPerUnit: number;
  contributionMarginPerUnit: number;
  contributionMarginFromCampaign: number;
  t: ReturnType<typeof useTranslations<"Calculators">>;
  tCommon: ReturnType<typeof useTranslations<"Common">>;
}

export function CampaignRoasCalculator() {
  const t = useTranslations("Calculators");
  const tCommon = useTranslations("Common");
  const form = useForm<z.infer<typeof formSchema>>({
    mode: "onChange",
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const breakevenRoas = floorToTwoDecimals(
    form.watch("netSellingPrice") / form.watch("grossProfitPerUnit")
  );

  const unitsSold =
    (form.watch("campaignSpend") * form.watch("campaignRoas")) /
    form.watch("netSellingPrice");

  const adSpendPerUnit = form.watch("campaignSpend") / unitsSold;

  const contributionMarginPerUnit =
    form.watch("grossProfitPerUnit") - adSpendPerUnit;

  const contributionMarginFromCampaign = contributionMarginPerUnit * unitsSold;

  const tableProps = {
    form,
    breakevenRoas,
    unitsSold,
    adSpendPerUnit,
    contributionMarginPerUnit,
    contributionMarginFromCampaign,
    t,
    tCommon,
  } satisfies ContentProps;

  return (
    <Form {...form}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <TableHeader>
          <p className="text-body-md text-text-on-dark sticky top-0 font-semibold">
            {t("campaignRoas")}
          </p>
        </TableHeader>
        <DesktopTable {...tableProps} />
      </form>
    </Form>
  );
}

function DesktopTable({
  form,
  breakevenRoas,
  unitsSold,
  adSpendPerUnit,
  contributionMarginPerUnit,
  contributionMarginFromCampaign,
  t,
  tCommon,
}: ContentProps) {
  return (
    <Table>
      <colgroup>
        <col className="w-3/4" />
        <col className="w-1/4" />
      </colgroup>
      <tbody className="bg-surface">
        <FormField
          control={form.control}
          name="netSellingPrice"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr>
                <LabelCell>
                  <UnstyledFormLabel>{t("netSellingPrice")}</UnstyledFormLabel>
                </LabelCell>
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
              </Tr>
            </FormItemWithoutDiv>
          )}
        />
        <FormField
          control={form.control}
          name="grossProfitPerUnit"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr className="border-b">
                <LabelCell>
                  <UnstyledFormLabel>
                    {t("grossProfitPerUnit")}
                  </UnstyledFormLabel>
                </LabelCell>
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
              </Tr>
            </FormItemWithoutDiv>
          )}
        />

        <Tr className="border-b">
          <LabelCell>{t("breakEvenRoas")}</LabelCell>
          <NumberDisplayCell>{breakevenRoas}</NumberDisplayCell>
        </Tr>

        <FormField
          control={form.control}
          name="campaignSpend"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr>
                <LabelCell>
                  <UnstyledFormLabel>{t("campaignSpend")}</UnstyledFormLabel>
                </LabelCell>
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
              </Tr>
            </FormItemWithoutDiv>
          )}
        />

        <FormField
          control={form.control}
          name="campaignRoas"
          render={({ field }) => (
            <FormItemWithoutDiv>
              <Tr>
                <LabelCell>
                  <UnstyledFormLabel>
                    {t("campaignRoasLabel")}
                  </UnstyledFormLabel>
                </LabelCell>
                <NumberDisplayCell>
                  <FormControl>
                    <CurrencyInput {...field} />
                  </FormControl>
                </NumberDisplayCell>
              </Tr>
            </FormItemWithoutDiv>
          )}
        />

        <Tr>
          <LabelCell>{t("unitsSold")}</LabelCell>
          <NumberDisplayCell>{unitsSold}</NumberDisplayCell>
        </Tr>

        <Tr className="border-b">
          <LabelCell>{t("adSpendPerUnit")}</LabelCell>
          <NumberDisplayCell>{adSpendPerUnit}</NumberDisplayCell>
        </Tr>

        <Tr>
          <LabelCell>{t("contributionMarginPerUnit")}</LabelCell>
          <NumberDisplayCell>{contributionMarginPerUnit}</NumberDisplayCell>
        </Tr>

        <Tr className="border-b">
          <LabelCell className="font-bold">
            {t("contributionMarginFromCampaign")}
          </LabelCell>
          <NumberDisplayCell className="font-bold">
            {contributionMarginFromCampaign}
          </NumberDisplayCell>
        </Tr>
      </tbody>
      <tfoot className="py-4">
        <Tr className="border-x-0 border-b-0 md:border-x-0">
          <td colSpan={3} className="px-5 py-3 text-center">
            <button
              type="submit"
              className="hidden"
              onClick={(e) => e.preventDefault()}
            />
            <Button
              onClick={() => {
                Object.entries(defaultValues).forEach(([key, value]) => {
                  form.setValue(key as keyof typeof defaultValues, value);
                });
              }}
              variant="link"
              size="sm"
              className="text-body"
            >
              {tCommon("clearResults")}
            </Button>
          </td>
        </Tr>
      </tfoot>
    </Table>
  );
}
